import throttle from 'lodash/throttle';

import { API_BASE_URL, EVENT_TAG_CREATE_THROTTLE_INTERVAL, NR_OF_ITEMS_PER_PAGE, SORT_KEYS } from '../constants/api';
import { ApiError } from './error';
import { bus, EVENT } from '@videotools/js/events';
import laroute from '@laroute';
import storage from '@shared/js/storage';

import Event from './entities/Event';

import { CHILD_MATCH_LEAGUE_ID } from '../constants/categories';
import TagGroup from '@videotools/js/api/entities/TagGroup';
import { EventFilter } from '@shared/enums/EventFilter';

const ApiClient = {
	defaultHeaders: {},
	client:         null,
	userContextId:  null,

	init(userContextId) {
		this.client = window.axios;

		if (userContextId) {
			this.userContextId = userContextId;
			this.setDefaultHeaders(userContextId);
		}

		this.setErrorsInterceptor();
	},
	setDefaultHeaders(userContextId) {
		this.defaultHeaders = {
			'Asport-User-Context-ID': userContextId,
		};
	},
	setErrorsInterceptor() {
		this.client.interceptors.response.use(
			response => response,
			(error) => {
				if (!error.config.suppressErrorToast) {
					bus.publish(EVENT.ERROR, error);
				}

				const response = error.response;
				const status = (typeof response === 'object' && response !== null) ? response?.status : -1;
				if (status == 401) {
					// redirect to login screen
					window.location = laroute.route('login');
				} else if (status > 0) {
					// if there is a server error show the error modal
					if (status >= 500 && status < 600) {
						bus.publish(EVENT.NETWORK_SERVER_ERROR, response);
					}
					// Do something with response error
					return Promise.reject(new ApiError(response));
				} else {
					if (error?.code === 'ERR_NETWORK') {
						bus.publish(EVENT.NETWORK_UNFORCED_CLIENT_ERROR, response);
					} else {
						// Unable to parse error response
						return Promise.reject(new Error(`Videotool ApiClient error: Load failed ${JSON.stringify(error)}`));
					}
				}
			}
		);
	},
	async get(url, params, rawUrl = false) {
		return await axios.get(rawUrl ? url : (API_BASE_URL + url), {
			params,
			headers: {
				...this.defaultHeaders,
			},
		});
	},
	async post(url, data, config, rawUrl = false) {
		return await this.client.post(rawUrl ? url : (API_BASE_URL + url), data, {
			...config,
			headers: {
				...this.defaultHeaders,
				...((config && config.headers) || {}),
			},
		});
	},
	async put(url, data, config, rawUrl = false) {
		return await this.client.put(rawUrl ? url : (API_BASE_URL + url), data, {
			...config,
			headers: {
				...this.defaultHeaders,
				...((config && config.headers) || {}),
			},
		});
	},
	async delete(url, params, rawUrl = false) {
		return await this.client.delete(rawUrl ? url : (API_BASE_URL + url), {
			params,
			headers: {
				...this.defaultHeaders,
			},
		});
	},
	async patch(url, data, config) {
		return await this.client.patch(API_BASE_URL + url, data, {
			...config,
			headers: {
				...this.defaultHeaders,
				...((config && config.headers) || {}),
			},
		});
	},
	// events
	async getEvent(eventId, relations) {
		const res = await this.get(`/event/${eventId}`, { relations });
		return res.data;
	},
	async getEventStream(eventId, password = null, userAgent = null) {
		const params = {};

		if (password && typeof password === 'string' && password.length > 0) {
			params.password = btoa(password);
		}

		if (userAgent) {
			this.defaultHeaders['x-cc-agent'] = userAgent;
		}
		const playoutUrl = `/api/v1/event/${eventId}/playout?viewerId=${storage.viewerId}`;
		const res = await this.get(playoutUrl, params, true);
		return res.data;
	},
	async getEventTags({ eventId, groupId = null, cached = false }, relations) {
		let url = `/event/${eventId}/tags`;

		if (cached) {
			url += '/archive';
		}

		if (groupId) {
			url += `/${groupId}`;
		}
		const res = await this.get(url, {
			page:      1,
			relations,
			sortOrder: { created_at: 'desc' },
		});
		return res.data?.data || [];
	},
	async getEventLineup(id) {
		const url = laroute.route('api.event.lineups', { event: id });
		const res = await this.get(url, null, true);
		return res.data || [];
	},
	createEventTag: throttle(async function(eventId, data) {
		const res = await this.post(`/event/${eventId}/tag`, data);
		return res.data;
	}, EVENT_TAG_CREATE_THROTTLE_INTERVAL),
	async deleteEventTag(eventId, tagId) {
		const res = await this.delete(`/event/${eventId}/tags/${tagId}`);
		return res.data;
	},
	async deleteEventTags(eventId, tagIds) {
		const res = await this.delete(`/event/${eventId}/tags`, {
			eventTagIds: JSON.stringify(tagIds),
		});
		return res.data;
	},
	async updateEventTag(eventId, tagId, data) {
		const patchData = [];
		for (const [k, v] of Object.entries(data)) {
			patchData.push({
				op:    'replace',
				path:  `/${k}`,
				value: v,
			});
		}
		const res = await this.patch(`/event/${eventId}/tags/${tagId}`, patchData);
		return res.data;
	},
	async uploadEventTagImage(eventId, tagId, image) {
		// convert image from base64 to blob
		const imageBlob = await (await fetch(image)).blob();
		const data = new FormData();
		data.set('image', imageBlob);
		const res = await this.post(`/event/${eventId}/tags/${tagId}/image`, data, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return res.data;
	},
	async updateEventOffset(eventId, offset) {
		const res = await this.patch(`/event/${eventId}`, [
			{
				op:    'replace',
				path:  '/tagOffsetSeconds',
				value: offset,
			},
		]);
		return res.data;
	},
	async getEventHighlightRules(eventId) {
		const res = await this.get(laroute.route('api.highlight_rules.event', { event: eventId, liveOnly: true, automaticOnly: true }), null, true);
		return res.data || [];
	},
	async getTeamRelations(teamId, relations = ['players']) {
		const res = await this.get(`/team/${teamId}`, { relations });
		const { league, players } = res.data;
		return relations ? { players, league } : { players };
	},
	/**
	 * Get events filtered by matchday
	 * @param {number} eventId
	 * @returns {Promise<import('./types/Event').Event[]>}
	 */
	async getMatchdayEvents(eventId) {
		const res = await this.get(laroute.route('api.events.summary'), {
			page:             1,
			perPage:          NR_OF_ITEMS_PER_PAGE,
			filters:          { categoryChildId: CHILD_MATCH_LEAGUE_ID, matchday: eventId },
			useContextFilter: false,
		}, true);
		const data = res.data.data;
		return data.map(event => new Event(event));
	},
	/**
	 * Get the league by team id
	 * @param {number} teamId
	 * @returns {Promise<import("./types/League").League>}
	 */
	async getLeagueByTeamId(teamId) {
		const res = await this.get(`/team/${teamId}`, { relations: 'league' });
		return res.data.league;
	},
	/**
	 * Get related events for an event id
	 * @param {number} eventId
	 * @param {string} [sortKey='recent']
	 * @param {number} [page=1]
	 * @returns {Promise<{meta : import("./types/Meta").Meta; data : import("./types/Event").Event[]}>}
	 */
	async getRelatedEvents(eventId, _sortKey = SORT_KEYS.RECENT, page = 1) {
		const res = await this.get(laroute.route('api.event.related', { event: eventId }), {
			page,
			perPage: NR_OF_ITEMS_PER_PAGE,
			filters: {
				[EventFilter.ARENA_AVAILABLE]: null,
			},
			useContextFilter: false,
		}, true);

		const { meta = {}, data = [] } = res?.data || {};
		return {
			meta,
			data: data.map(video => new Event(video)),
		};
	},
	/**
	 * Get videos
	 * @param {string} [query=''] - the query to search for a video
	 * @param {number} [page=1]
	 * @returns {Promise<{meta : import("./types/Meta").Meta; data : import("./types/Event").Event[]}>}
	 */
	async getVideos(query = '', page = 1) {
		const res = await this.get('/events', {
			sortOrder: { start: 'desc' },
			page,
			perPage:   NR_OF_ITEMS_PER_PAGE,
			filters:   { video: null, search: query },
		});
		return res.data;
	},
	async getEditorAssetVideos() {
		const res = await this.get(laroute.route('api.events.editor_assets'), {
			useContextFilter: true,
		}, true);
		return res.data;
	},
	async buildVideo(eventParams = {}, parts = [], copyTags = false, enableAudioFade = false, config = {}) {
		const { data } = await this.post(laroute.route('api.video_build.create'), {
			event:      eventParams,
			buildParts: parts,
			copyTags,
			enableAudioFade,
		}, config, true);

		return data;
	},
	async timeShiftEventTags(eventId, tagIds, offsetSeconds) {
		if (!eventId || !offsetSeconds) {
			return null;
		}
		const payload = {
			offsetSeconds,
			tagIds,
		};
		const { data } = await this.put(`/event/${eventId}/tags/offset`, payload);

		return data;
	},
	async setEventPeriodStartMinutes(eventId, values) {
		// [0,15] -> first period starts at 0 minutes, second at 15 minutes and so on...
		const res = await this.patch(`/event/${eventId}`, [
			{
				op:    'replace',
				path:  '/periodStartTimes',
				value: values,
			},
		]);
		return res.data;
	},
	async setScoreboardSettings(eventId, showScore = false, showTime = false) {
		const res = await this.patch(`/event/${eventId}`, [
			{
				op:    'replace',
				path:  '/showScoreboardScore',
				value: showScore,
			},
			{
				op:    'replace',
				path:  '/showScoreboardTime',
				value: showTime,
			},
		]);
		return res.data;
	},
	async setCustomPlayerText(eventId, value) {
		const res = await this.patch(`/event/${eventId}`, [
			{
				op:   'replace',
				path: '/playerCustomText',
				value,
			},
		]);
		return res.data;
	},
	async setCustomPlayerTextEnabled(eventId, value) {
		const res = await this.patch(`/event/${eventId}`, [
			{
				op:   'replace',
				path: '/playerCustomTextEnabled',
				value,
			},
		]);
		return res.data;
	},
	/**
	 * Gets the tag groups filtered by either sportId or eventId.
	 *
	 * @param sportId returns all the tag groups of that sportId for the current user
	 * @param eventId returns all the tag groups for the event's sportId and participants that are in the same club with the current user (takes precedence over the sportId)
	 * @return {Promise<*>}
	 */
	async getTagGroups(eventId) {
		if (!eventId) {
			throw new Error('Missing eventId');
		}

		const { data } = await this.get(`/event/${eventId}/tag_groups`);

		return data.map(props => new TagGroup(props));
	},
	async createTagGroup(sportId, props) {
		const { data } = await this.post('/tag_group', { sport_id: sportId, ...props });

		return data;
	},
	async updateTagGroup(id, props) {
		const { data } = await this.patch(`/tag_group/${id}`, props);

		return data;
	},
	async deleteTagGroup(id) {
		await this.delete(`/tag_group/${id}`);
	},
	async createCustomButtons(tagGroupId, newCustomButtons = []) {
		if (newCustomButtons.length) {
			const { data } = await this.patch(`/tag_group/${tagGroupId}/buttons`,
				{
					add: newCustomButtons.map(({ name, secondsBefore, secondsAfter, target }) => ({ name, secondsBefore, secondsAfter, target })),
				}
			);

			return data;
		}
		return {};
	},
	async updateCustomButtons(tagGroupId, customButtons = {}) {
		const buttonsData = {
			add: (customButtons.added || [])
				        .map(({ name, secondsBefore, secondsAfter, target }) => ({ name, secondsBefore, secondsAfter, target })),
			update: (customButtons.updated || [])
				        .map(({ id, name, secondsBefore, secondsAfter }) => ({ id, name, secondsBefore, secondsAfter })),
			remove: (customButtons.deleted || [])
				        .map(({ id }) => ({ id })),
		};
		const { data } = await this.patch(`/tag_group/${tagGroupId}/buttons`, buttonsData);
		return data;
	},
	async likeEventTag(tagId, eventId) {
		return await this.put(`/event/${eventId}/tags/${tagId}/like`);
	},
	async getEventAds(eventId) {
		const res = await this.get(`/event/${eventId}/overlays`);
		if (res && res.data && res.data.data) {
			// Attach current timestamp to imageUrl to ensure subsequent requests to the same asset are cached
			res.data.data = res.data.data.map((item) => {
				item.imageUrl = item.imageUrl ? item.imageUrl + '?timestamp=' + Date.now() : null;
				return item;
			});

			return res.data;
		} else {
			return [];
		}
	},
	async getTagTemplates(eventId) {
		if (!eventId) {
			throw new Error('Missing eventId');
		}
		const { data } = await this.get(laroute.route('api.event.tag_templates', { event: eventId }), null, true);

		return data;
	},
	async deleteEntryOnTagTemplate(eventId, templateId, tagId) {
		const { data } = await this.delete(laroute.route('api.event.tag_template.item.delete', {
			event:           eventId,
			tagTemplate:     templateId,
			tagTemplateItem: tagId,
		}), null, true);

		return data;
	},
	async startHighlightDetection(eventId, tagGroupId, settings = {}) {
		if (!eventId) {
			throw new Error('Missing eventId');
		}
		if (!tagGroupId) {
			throw new Error('Missing tagGroupId');
		}
		const payload = Object.assign({
			mode:       'full',
			type:       'highlights',
			tagGroupId: tagGroupId,
		}, settings);

		const { data } = await this.post(laroute.route('api.audio_detection.init', {
			event: eventId,
		}), payload, { suppressErrorToast: true }, true);

		return data;
	},
	async getHighlightDetectionStatus(jobId) {
		const { data } = await this.get(laroute.route('api.audio_detection.job.status', {
			jobId: jobId,
		}), null, true);

		return data;
	},
	async cancelHighlightDetectionJob(jobId) {
		const { data } = await this.put(laroute.route('api.audio_detection.job.cancel', {
			jobId: jobId,
		}), {}, null, true);

		return data;
	},
	async getHighlightDetectionStatusByEvent(event) {
		const { data } = await this.get(laroute.route('api.audio_detection.event.status', {
			event,
		}), null, true);

		return data;
	},

	async getEventMultiFormat(eventId, tagId) {
		const { data } = await this.get(`${laroute.route('api.event.multiformat', { event: eventId })}?eventTagId=${tagId}`, null, true);
		return data;
	},

	// Graphics overlays
	async getGraphicSets() {
		const { data } = await this.get(laroute.route('api.graphic.sets'), { relations: ['graphics.tags'] }, true);
		return data;
	},
};

export default ApiClient;
